import { useMemo, useState, useCallback, useEffect } from "react";
import { useAccount } from "wagmi";
import { useDispatch, useSelector } from "react-redux";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useVaultTvlApy } from "@hooks/useVaultTvlApy";
import useSanction from "@hooks/useSanction";
import { ETH, RSETH, WRSETH } from "@config/contractsConfig";
import {
  CONNECT_WALLET_CLICK,
  VAULT_MAX_DEPOSIT_CLICK,
  VAULT_MAX_WITHDRAW_CLICK,
  VAULT_WITHDRAW_CLICK,
} from "@constants/analytics";
import {
  toFixedWithoutRounding,
  MAX_FRACTION_DIGITS,
  CONNECT_WALLET_CTA,
  isNumber,
} from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";
import useDappConfig from "@hooks/useDappConfig";
import useDappChain from "@hooks/useDappChain";
import useTokenBalance from "@hooks/useTokenBalance";
import useExchangeRate from "@hooks/useExchangeRate";
import useVaultAllowance from "@hooks/useVaultAllowance";
import useAgEthExchangeRate from "@hooks/useAgEthExchangeRate";
import useEthConversion from "@hooks/useEthConversion";
import { updateUserData } from "@store";
import { isFloat, toStringPrecision, trackEvent, emitReduxEvent } from "utils";
import { AIRDROP_VAULT_STRINGS } from "./constants";
import { useEstimatedGasFee } from "@hooks/useGasFeeEstimation";

export const useAgEthVault = () => {
  const {
    isLoadingApy = false,
    formattedApy = "-",
    formattedTvl = "-",
    isLoadingTvl = false,
    formattedETHStaked = "",
  } = useVaultTvlApy("agETH");

  const { isSanctioned } = useSanction();

  const vaultHeaderData = useMemo(
    () => ({
      isLoadingAPYData: isLoadingApy,
      isLoadingTotalStaked: isLoadingTvl,
      totalStaked:
        formattedTvl && formattedTvl !== "-" ? `$${formattedTvl}` : "-",
      totalStakedEth: formattedETHStaked,
      apyValue: formattedApy,
      apyLabel: AIRDROP_VAULT_STRINGS.HEADER.STATS.APY,
      totalStakedLabel: AIRDROP_VAULT_STRINGS.HEADER.STATS.TVL,
      title: AIRDROP_VAULT_STRINGS.HEADER.TITLE,
      logo: {
        src: "/assets/agETH.png",
        label: "agETH Logo",
      },
      auditedUrl:
        "https://kerneldao.gitbook.io/kernel/getting-started/gain/airdrop-gain#audits",
    }),
    [isLoadingApy, isLoadingTvl, formattedTvl, formattedETHStaked, formattedApy]
  );

  const tabsData = useMemo(
    () => AIRDROP_VAULT_STRINGS.SECTIONS.DEPOSIT_WITHDRAW.TABS,
    []
  );

  const bridgeConfig = useMemo(
    () => AIRDROP_VAULT_STRINGS.SECTIONS.DEPOSIT_WITHDRAW.BRIDGE,
    []
  );

  const portfolioConfig = useMemo(
    () => ({
      tabs: AIRDROP_VAULT_STRINGS.SECTIONS.PORTFOLIO.TABS,
      points: AIRDROP_VAULT_STRINGS.SECTIONS.PORTFOLIO.POINTS,
      overviewData: AIRDROP_VAULT_STRINGS.SECTIONS.OVERVIEW.DATA,
      aprReports: AIRDROP_VAULT_STRINGS.SECTIONS.PORTFOLIO.APR_REPORTS,
    }),
    []
  );

  return {
    vaultHeaderData,
    tabsData,
    bridgeConfig,
    portfolioConfig,
    isSanctioned,
    constants: AIRDROP_VAULT_STRINGS,
  };
};

// Airdrop Vault specific deposit logic
export const useAgEthDeposit = () => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { isConnected, address } = useAccount();
  const { isVaultSupportedChain, config, vaultConfig } = useDappConfig();
  const { erc20Allowance } = useVaultAllowance();
  const {
    tokensBalances,
    xTokenVaultBalance,
    xTokenTokenBalance,
    nativeTokenBalance,
  } = useTokenBalance();

  const { estimatedGasFee } = useEstimatedGasFee();

  const { selectedERC20Vault, referralId } = useSelector(
    (state: any) => state.user
  );
  const dispatch = useDispatch();
  const { openConnectModal } = useConnectModal();

  const tokenBalance = useMemo(() => {
    if (selectedERC20Vault === config.xtoken.symbol) return xTokenTokenBalance;
    else if (selectedERC20Vault === ETH) return nativeTokenBalance;
    return tokensBalances[selectedERC20Vault];
  }, [xTokenVaultBalance, tokensBalances, selectedERC20Vault]);

  const checkInput = useCallback(
    (value = amount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenBalance) {
        error = `You can't deposit more than available balance in your wallet.`;
      }
      return error;
    },
    [isConnected, tokenBalance, selectedERC20Vault, amount, tokensBalances]
  );

  useEffect(() => {
    if (amount !== "") {
      const error = checkInput(amount);
      setError(error);
    }
  }, [tokenBalance, address, amount, checkInput]);

  const isApproved = useMemo(() => {
    if (selectedERC20Vault === ETH) return true;
    return (
      erc20Allowance &&
      Number(erc20Allowance) &&
      Number(erc20Allowance) >= Number(amount)
    );
  }, [erc20Allowance, amount, selectedERC20Vault]);

  const conversionsDataAgEth = useAgEthExchangeRate();
  const conversionDataRsEth = useExchangeRate("vault");
  const { valueInEth } = useEthConversion(conversionsDataAgEth?.toERC20);

  const conversionsRsETH = useMemo(() => {
    const conversionToERC20 = conversionDataRsEth?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
      fromERC20: conversionToERC20 ? 1 / conversionToERC20 : 0,
    };
  }, [conversionDataRsEth?.exchangeRate]);

  const getAgEthExchange = useCallback(() => {
    const rsETHRate =
      selectedERC20Vault === RSETH || selectedERC20Vault === WRSETH
        ? 1
        : Number(conversionsRsETH?.toERC20);
    return Number(conversionsDataAgEth?.toERC20) * rsETHRate;
  }, [
    conversionsDataAgEth?.toERC20,
    conversionsRsETH?.toERC20,
    selectedERC20Vault,
  ]);

  const exchangeRate = getAgEthExchange();

  const convertedAmount = useMemo(() => {
    const agRate = getAgEthExchange();
    if (amount && isNumber(amount)) {
      const value = Number(amount) / (agRate > 0 ? agRate : 1);
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [amount, getAgEthExchange]);

  const handleClick = useCallback(
    (inputAmount?: string) => {
      const amountToUse = inputAmount || amount;

      if (!isConnected) {
        openConnectModal?.();
        trackEvent(CONNECT_WALLET_CLICK, {
          cta_location: "airdrop_vault_deposit_cta",
          cta: CONNECT_WALLET_CTA,
        });
      } else if (!isVaultSupportedChain) {
        emitReduxEvent(EVENTS.SWITCH_NETWORK);
      } else {
        if (isApproved) {
          if (!amountToUse) {
            setError(
              "This field can't be blank or zero. Please provide valid input."
            );
            return;
          }

          emitReduxEvent(EVENTS.HANDLE_VAULT_DEPOSIT, {
            amount: amountToUse,
            referralId,
          });

          setAmount("");
        } else {
          emitReduxEvent(EVENTS.HANDLE_VAULT_APPROVE, { amount: amountToUse });
        }
      }
    },
    [
      isConnected,
      isApproved,
      isVaultSupportedChain,
      amount,
      openConnectModal,
      referralId,
    ]
  );

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleMaxClick = useCallback(() => {
    trackEvent(VAULT_MAX_DEPOSIT_CLICK);
    if (selectedERC20Vault === ETH) {
      const amount = Math.max(
        0,
        tokenBalance - (Number(estimatedGasFee) || 0)
      ).toString();
      handleChange(toFixedWithoutRounding(amount.toString(), 4));
    } else {
      handleChange(toFixedWithoutRounding(tokenBalance.toString(), 4));
    }
  }, [tokenBalance, estimatedGasFee, selectedERC20Vault]);

  const handleTokenSelect = (key: string) => {
    dispatch(
      updateUserData({
        ["selectedERC20Vault"]: key,
      })
    );
  };

  return {
    amount,
    setAmount,
    error,
    setError,
    tokenBalance,
    isConnected,
    isApproved,
    convertedAmount,
    exchangeRate,
    selectedERC20Vault,
    vaultConfig,
    xTokenVaultBalance,
    conversionsDataAgEth,
    valueInEth,
    handleClick,
    handleChange,
    handleMaxClick,
    handleTokenSelect,
    isVaultSupportedChain,
  };
};

// Airdrop Vault specific withdraw logic
export const useAgEthWithdraw = () => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { isConnected, address } = useAccount();
  const { isEthereumChain } = useDappChain();
  const { vaultConfig, isVaultWithdrawSupportedChain } = useDappConfig();
  const { xTokenVaultBalance, xTokenTokenBalance } = useTokenBalance();
  const { openConnectModal } = useConnectModal();

  const tokenBalance = xTokenVaultBalance;

  const checkInput = useCallback(
    (value = amount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenBalance) {
        error = `You can't withdraw more than available balance in your wallet.`;
      }
      return error;
    },
    [isConnected, tokenBalance, amount]
  );

  useEffect(() => {
    if (amount !== "") {
      const error = checkInput(amount);
      setError(error);
    }
  }, [tokenBalance, address, amount, checkInput]);

  const conversionsDataAgEth = useAgEthExchangeRate();
  const { valueInEth } = useEthConversion(conversionsDataAgEth?.toERC20);

  const exchangeRate = Number(conversionsDataAgEth?.toERC20);

  const convertedAmount = useMemo(() => {
    if (amount && isNumber(amount)) {
      const value = Number(amount) * Number(conversionsDataAgEth?.toERC20);
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [amount, conversionsDataAgEth]);

  const handleClick = useCallback(
    (inputAmount?: string) => {
      const amountToUse = inputAmount || amount;

      if (!isConnected) {
        openConnectModal?.();
        trackEvent(CONNECT_WALLET_CLICK, {
          cta_location: "airdrop_vault_withdraw_cta",
          cta: CONNECT_WALLET_CTA,
        });
      } else if (!isEthereumChain) {
        emitReduxEvent(EVENTS.SWITCH_NETWORK);
      } else {
        if (!amountToUse) {
          setError(
            "This field can't be blank or zero. Please provide valid input."
          );
          return;
        }

        trackEvent(VAULT_WITHDRAW_CLICK, { amount: amountToUse });
        emitReduxEvent(EVENTS.HANDLE_VAULT_WITHDRAW, { amount: amountToUse });
        setAmount("");
      }
    },
    [isConnected, isEthereumChain, amount, openConnectModal]
  );

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleMaxClick = useCallback(() => {
    trackEvent(VAULT_MAX_WITHDRAW_CLICK);
    setAmount(toFixedWithoutRounding(tokenBalance.toString(), 4));
  }, [tokenBalance]);

  return {
    amount,
    setAmount,
    error,
    setError,
    tokenBalance,
    isConnected,
    convertedAmount,
    exchangeRate,
    vaultConfig,
    xTokenTokenBalance,
    conversionsDataAgEth,
    valueInEth,
    handleClick,
    handleChange,
    handleMaxClick,
    isVaultSupportedChain: isVaultWithdrawSupportedChain,
  };
};
